import { useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Heart, MessageCircle, Award, Send, SmilePlus, MoreVertical, Pin, Trash2, PinOff, Megaphone, ExternalLink, Flag } from "lucide-react";
import ReportDialog from "../ReportDialog";
import Avatar from "./Avatar";
import TrimmedVideo from "./TrimmedVideo";
import { themeGradient, DEFAULT_THEME } from "../../lib/themes";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

export const REACTION_STICKERS = ["😍", "😂", "🎉", "👏", "🌟", "🦄", "🐶", "❤️"];

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

export default function PostCard({ post, user, onLike, onComment, onReact, onPin, onDelete }) {
  const navigate = useNavigate();
  const [comment, setComment] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const liked = post.likes?.includes(user?.user_id);
  const theme = post.author_theme || DEFAULT_THEME;
  const author = { name: post.author_name, picture: post.author_picture };
  const reactions = post.reactions || {};
  const myReaction = reactions[user?.user_id];
  const counts = Object.values(reactions).reduce((acc, s) => { acc[s] = (acc[s] || 0) + 1; return acc; }, {});
  const mine = post.author_user_id && post.author_user_id === user?.user_id;
  const canManage = mine && (onPin || onDelete);
  const canReport = !mine && !post.sponsored && !!post.author_user_id;
  const [reportTarget, setReportTarget] = useState(null);
  const goToAuthor = () => {
    if (post.author_user_id && post.author_user_id !== user?.user_id) navigate(`/social/u/${post.author_user_id}`);
  };
  const react = (sticker) => { setPickerOpen(false); onReact && onReact(post, sticker); };
  const goAuthor = () => { if (!post.sponsored) goToAuthor(); };
  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      className={`bg-white rounded-3xl border shadow-lg overflow-hidden ${post.sponsored ? "border-amber-300" : "border-amber-100/60"}`} data-testid={`post-${post.post_id}`}>
      <div className="h-1.5" style={{ background: themeGradient(theme) }} data-testid={`post-theme-strip-${post.post_id}`} />
      {post.sponsored && (
        <div className="px-5 pt-3 flex items-center gap-1.5 text-xs font-bold text-amber-600 uppercase tracking-wide" data-testid={`sponsored-badge-${post.post_id}`}>
          <Megaphone className="h-3.5 w-3.5" /> Sponsored
        </div>
      )}
      {post.pinned && !post.sponsored && (
        <div className="px-5 pt-3 flex items-center gap-1.5 text-xs font-bold text-amber-600" data-testid={`pinned-badge-${post.post_id}`}>
          <Pin className="h-3.5 w-3.5 fill-amber-500" /> Pinned post
        </div>
      )}
      <div className="p-5 pt-3">
        <div className="flex items-center gap-3">
          <Avatar user={author} size={44} onClick={goAuthor} testid={`post-author-avatar-${post.post_id}`} />
          <div>
            <button onClick={goAuthor} className={`font-bold text-left ${post.sponsored ? "cursor-default" : "hover:underline"}`} style={{ color: theme.primary }} data-testid={`post-author-${post.post_id}`}>
              {post.author_name}
            </button>
            <div className="text-xs text-slate-400">{post.sponsored ? "Sponsored content" : timeAgo(post.created_at)}</div>
          </div>
          <div className="ml-auto flex items-center gap-2">
            {post.badge && (
              <span className="inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-bold text-white"
                style={{ backgroundColor: theme.accent }}>
                <Award className="h-3.5 w-3.5" /> {post.badge}
              </span>
            )}
            {(canManage || canReport) && (
              <Popover open={menuOpen} onOpenChange={setMenuOpen}>
                <PopoverTrigger asChild>
                  <button data-testid={`post-menu-btn-${post.post_id}`} className="h-8 w-8 rounded-full hover:bg-slate-100 flex items-center justify-center text-slate-500">
                    <MoreVertical className="h-4 w-4" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" className="w-44 p-1 rounded-2xl" data-testid={`post-menu-${post.post_id}`}>
                  {canManage && onPin && (
                    <button onClick={() => { setMenuOpen(false); onPin(post); }} data-testid={`pin-post-${post.post_id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 rounded-xl">
                      {post.pinned ? <><PinOff className="h-4 w-4" /> Unpin</> : <><Pin className="h-4 w-4" /> Pin to profile</>}
                    </button>
                  )}
                  {canManage && onDelete && (
                    <button onClick={() => { setMenuOpen(false); onDelete(post); }} data-testid={`delete-post-${post.post_id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-red-600 hover:bg-red-50 rounded-xl">
                      <Trash2 className="h-4 w-4" /> Delete post
                    </button>
                  )}
                  {canReport && (
                    <button onClick={() => { setMenuOpen(false); setReportTarget({ type: "post", id: post.post_id }); }} data-testid={`report-post-${post.post_id}`}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm font-semibold text-rose-600 hover:bg-rose-50 rounded-xl">
                      <Flag className="h-4 w-4" /> Report post
                    </button>
                  )}
                </PopoverContent>
              </Popover>
            )}
          </div>
        </div>
        <p className="mt-4 text-slate-700 leading-relaxed whitespace-pre-wrap">{post.text}</p>
      </div>
      {post.video ? (
        <TrimmedVideo src={post.video} start={post.trim_start} end={post.trim_end}
          className="w-full max-h-96 bg-black object-contain" testid={`post-video-${post.post_id}`} />
      ) : post.image ? (
        <img src={post.image} alt="" className="w-full max-h-96 object-cover" />
      ) : null}

      {post.sponsored && post.cta_url && (
        <div className="px-5 pt-4">
          <a href={post.cta_url} target="_blank" rel="noreferrer" data-testid={`sponsored-cta-${post.post_id}`}
            className="flex items-center justify-center gap-2 rounded-2xl bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 transition">
            {post.cta_text || "Learn more"} <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      )}

      {/* Reaction chips */}
      {Object.keys(counts).length > 0 && (
        <div className="px-5 pt-3 flex flex-wrap gap-1.5" data-testid={`reactions-${post.post_id}`}>
          {Object.entries(counts).map(([s, c]) => (
            <button key={s} onClick={() => react(s)}
              className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-sm border transition ${myReaction === s ? "border-[#FF5A79] bg-pink-50" : "border-slate-200 bg-slate-50 hover:border-slate-300"}`}
              data-testid={`reaction-chip-${post.post_id}-${s}`}>
              <span>{s}</span><span className="text-xs font-bold text-slate-500">{c}</span>
            </button>
          ))}
        </div>
      )}

      <div className="px-5 py-3 flex items-center gap-5 border-t border-slate-100 mt-3">
        <button data-testid={`like-btn-${post.post_id}`} onClick={() => onLike(post)}
          className="flex items-center gap-1.5 text-sm font-semibold transition"
          style={{ color: liked ? theme.primary : undefined }}>
          <Heart className="h-5 w-5" style={liked ? { fill: theme.primary, color: theme.primary } : undefined} /> {post.likes?.length || 0}
        </button>
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button data-testid={`react-btn-${post.post_id}`}
              className={`flex items-center gap-1.5 text-sm font-semibold transition ${myReaction ? "text-[#FF5A79]" : "text-slate-500 hover:text-[#FF5A79]"}`}>
              {myReaction ? <span className="text-lg leading-none">{myReaction}</span> : <SmilePlus className="h-5 w-5" />} React
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-2 rounded-2xl" data-testid={`sticker-picker-${post.post_id}`}>
            <div className="flex gap-1">
              {REACTION_STICKERS.map((s) => (
                <button key={s} onClick={() => react(s)} data-testid={`sticker-${post.post_id}-${s}`}
                  className={`text-2xl p-1.5 rounded-xl hover:bg-slate-100 transition ${myReaction === s ? "bg-pink-50 ring-2 ring-[#FF5A79]" : ""}`}>
                  {s}
                </button>
              ))}
            </div>
          </PopoverContent>
        </Popover>
        <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-500">
          <MessageCircle className="h-5 w-5" /> {post.comments?.length || 0}
        </span>
      </div>
      {post.comments?.length > 0 && (
        <div className="px-5 pb-3 space-y-2">
          {post.comments.map((c) => (
            <div key={c.comment_id} className="flex gap-2 text-sm">
              <span className="font-bold text-slate-700">{c.name}</span>
              <span className="text-slate-500">{c.text}</span>
            </div>
          ))}
        </div>
      )}
      <form className="px-5 pb-5 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (comment.trim()) { onComment(post, comment); setComment(""); } }}>
        <Input data-testid={`comment-input-${post.post_id}`} value={comment} onChange={(e) => setComment(e.target.value)}
          placeholder="Write a kind comment…" className="rounded-full h-10" />
        <Button type="submit" size="icon" data-testid={`comment-submit-${post.post_id}`} className="rounded-full h-10 w-10 bg-[#2AB7CA] hover:bg-[#239ba9] shrink-0">
          <Send className="h-4 w-4" />
        </Button>
      </form>
      <ReportDialog target={reportTarget} onClose={() => setReportTarget(null)} />
    </motion.div>
  );
}

export { PostCard };
